/**
 * 기사 작성 (운영자 › 콘텐츠).
 *
 * 업체명·대표자명·질문·업종으로 기사 초안을 만들고, 광고주에게 검토를 요청한다. 광고주가 고치고 사진을 붙여
 * 승인하면 운영자에게 알림이 오고, 이 화면에서 보낸 원고와 광고주 수정본을 대조해 본다.
 * 상태: 작성 중(draft) → 광고주 검토 중(sent) → 승인됨(approved) → 게재 완료(published). 운영자는 작성 중일 때만 고친다.
 * 승인되면 운영자가 배포 대행사(mediap.kr)에 배포를 신청하고, 게재 확인(매일 11시 · "게재 확인" 버튼)이
 * 대행사의 `노출완료` 신청을 제목으로 맞춰 언론사·제목·링크를 붙인다. 거기서 못 가져온 게재는 기사 화면에서 직접 넣는다.
 * ★ 고객을 고르면 업체명·대표자명·목표 질문이 채워지지만 **전부 손으로 고칠 수 있다** — 기사에는 화면의 값이 들어간다.
 * ★ 생성은 동기다(수십 초). 버튼을 잠그고 기다리게 한다 — 두 번 누르면 두 번 과금된다.
 */
import {
  ARTICLE_INDUSTRY_PRESETS,
  type ArticlePublication,
  type ArticleNotice,
  type ArticleSummary,
  inferIndustry,
} from '@geo-admin/shared/contract/api/article.ts';
import { article } from '@geo-admin/shared/contract/api/index.ts';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { type FormEvent, useEffect, useState } from 'react';
import { ArticleBoard } from '../components/ArticleBoard.tsx';
import {
  ArticleDiff,
  ArticlePreview,
  ArticleProgressBadge,
  ArticleStatusBadge,
  PhotoGrid,
  PublicationList,
} from '../components/ArticleParts.tsx';
import { DataTable } from '../components/data-table.tsx';
import { PageTitle } from '../components/PageTitle.tsx';
import { BrandKindTabs, BrandOptions, useBrandChoices, useBrandKind } from '../components/BrandKindTabs.tsx';
import { Button } from '../components/ui/button.tsx';
import { Badge, Card, CardBody, CardHeader } from '../components/ui/card.tsx';
import { FieldError, Input, Label, Select, Textarea } from '../components/ui/form.tsx';
import { ErrorBox, Loading } from '../components/ui/states.tsx';
import { useApi, useApiMutation } from '../lib/api.ts';
import { dateTime } from '../lib/format.ts';

const CUSTOM = '__custom__';
/** 승인된 기사의 언론사 배포 신청 페이지 */
const DEPLOY_ORDER_URL = 'https://mediap.kr/newsix/order3';

export function ArticlesPage() {
  // 「실제 | 테스트」 탭 — 기사 목록·이번 회차 보드가 탭을 따른다(261023-test-brand). 고객 없는 기사는 실제 탭에 있다
  const brandKind = useBrandKind();
  const list = useApi(article.listArticles, { query: { brandKind } });

  const columns: ColumnDef<ArticleSummary, unknown>[] = [
    {
      header: '제목',
      accessorKey: 'title',
      cell: ({ row }) => (
        <Link to="/admin/articles/$id" params={{ id: row.original.id }} className="font-medium hover:underline">
          {row.original.title}
        </Link>
      ),
    },
    { header: '업체', accessorKey: 'companyName' },
    { header: '업종', accessorKey: 'industry' },
    { header: '질문', accessorKey: 'query' },
    {
      header: '상태',
      accessorKey: 'status',
      // 배지 하나 — 게재 완료 > 대행사 진행(기사접수 …) > 우리 상태
      cell: ({ row }) => <ArticleProgressBadge status={row.original.status} pressStatus={row.original.pressStatus} />,
    },
    { header: '만든 날', accessorKey: 'createdAt', cell: ({ row }) => dateTime(row.original.createdAt) },
  ];

  // 한 편 쓰기 — 늘 펼쳐 두지 않고 버튼·표의 "직접 쓰기"로 연다. 직접 쓰기는 그 고객으로 채워 연다(key 로 새로 띄운다)
  const [form, setForm] = useState<{ brandId: string } | null>(null);
  const [tab, setTab] = useState<'cycle' | 'all'>('cycle');
  const openForm = (brandId = '') => {
    setForm({ brandId });
    requestAnimationFrame(() => document.getElementById('new-article')?.scrollIntoView({ behavior: 'smooth' }));
  };

  return (
    <>
      <PageTitle
        title="기사 작성"
        description="기사는 회차마다 고객 한 곳에 한 편입니다. 이번 회차에 아직 안 쓴 고객을 골라 일괄로 쓰거나 한 편씩 씁니다"
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <PressSyncBar />
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.open(DEPLOY_ORDER_URL, '_blank', 'noopener,noreferrer')}
            >
              배포신청
            </Button>
            <Button size="sm" onClick={() => openForm()}>
              + 한 편 쓰기
            </Button>
          </div>
        }
      />
      <BrandKindTabs />
      {form ? (
        <div id="new-article">
          <NewArticleForm key={form.brandId} initialBrandId={form.brandId} onClose={() => setForm(null)} />
        </div>
      ) : null}
      <Card>
        <div className="flex gap-1 border-slate-200 border-b px-4 pt-3">
          {(
            [
              ['cycle', '이번 회차'],
              ['all', '전체 기사'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={
                tab === key
                  ? '-mb-px border-slate-900 border-b-2 px-3 py-2 font-semibold text-slate-900 text-sm'
                  : 'px-3 py-2 text-slate-500 text-sm hover:text-slate-900'
              }
            >
              {label}
            </button>
          ))}
        </div>
        <CardBody>
          {tab === 'cycle' ? (
            <ArticleBoard brandKind={brandKind} onWriteManually={openForm} />
          ) : list.error ? (
            <ErrorBox error={list.error} />
          ) : !list.data ? (
            <Loading />
          ) : (
            // 지난 회차·등록 안 된 업체 기사까지 — 최근 200건
            <DataTable columns={columns} data={list.data.articles} empty="아직 작성한 기사가 없습니다" />
          )}
        </CardBody>
      </Card>
    </>
  );
}

/**
 * 게재 확인 — 배포 대행사(mediap.kr) 보도신청목록의 `노출완료` 를 기사에 붙인다.
 * 매일 11시에 저절로 돌고, 기다리지 않으려면 여기서 누른다.
 */
function PressSyncBar() {
  const got = useApi(article.getPressSync);
  const run = useApiMutation(article.runPressSync);
  const [error, setError] = useState<string | null>(null);
  const last = run.data?.last ?? got.data?.last ?? null;

  const onRun = async () => {
    setError(null);
    try {
      await run.mutateAsync({});
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-end gap-2 text-slate-500 text-xs">
      {error ? (
        <span className="text-bad">{error}</span>
      ) : last ? (
        <span title={last.error ?? undefined}>
          마지막 게재 확인 {dateTime(last.at)}
          {last.ok ? ` · 새 게재 ${last.attached ?? 0}건` : ' · 실패'}
          {last.manual ? '' : ' (자동)'}
        </span>
      ) : got.data ? (
        <span>{got.data.configured ? '매일 11시에 게재를 확인합니다' : 'mediap 계정이 설정되지 않았습니다'}</span>
      ) : null}
      <Button
        size="sm"
        variant="outline"
        onClick={() => void onRun()}
        disabled={run.isPending || got.data?.configured === false}
      >
        {run.isPending ? '확인 중…' : '게재 확인'}
      </Button>
    </div>
  );
}

/** 한 편씩 쓴다 — 표에 없는 업체(등록 안 된 곳)나 일괄로 못 쓰는 고객, 다시 쓸 때 */
function NewArticleForm({ initialBrandId = '', onClose }: { initialBrandId?: string; onClose?: () => void }) {
  const navigate = useNavigate();
  const brands = useBrandChoices();
  const generate = useApiMutation(article.generateArticle);

  const [brandId, setBrandId] = useState(initialBrandId);
  const [companyName, setCompanyName] = useState('');
  const [representativeName, setRepresentativeName] = useState('');
  const [query, setQuery] = useState('');
  // 업종은 업체명에서 자동으로 정한다(inferIndustry). 직접 고르는 값은 자동이 못 정했거나 운영자가 바꿀 때만 쓴다
  const [industryManual, setIndustryManual] = useState(false);
  const [industryPick, setIndustryPick] = useState('');
  const [industryCustom, setIndustryCustom] = useState('');
  const [error, setError] = useState<string | null>(null);

  const source = useApi(article.articleSource, { query: { brandId } }, { enabled: brandId !== '' });
  // 고객을 바꿨을 때 한 번 채운다. 운영자가 고친 값을 다시 덮지 않게 데이터가 바뀔 때만 돈다
  useEffect(() => {
    if (!source.data) return;
    setCompanyName(source.data.companyName);
    setRepresentativeName(source.data.representativeName ?? '');
    setQuery(source.data.queries[0] ?? '');
  }, [source.data]);

  const autoIndustry = inferIndustry(companyName);
  const useAuto = !industryManual && autoIndustry !== null;
  const industry = useAuto ? autoIndustry : industryPick === CUSTOM ? industryCustom.trim() : industryPick;
  /** 자동 값을 바꾸려 할 때 — 그 값에서 출발한다 */
  const overrideIndustry = () => {
    setIndustryManual(true);
    if (autoIndustry && (ARTICLE_INDUSTRY_PRESETS as readonly string[]).includes(autoIndustry)) {
      setIndustryPick(autoIndustry);
    } else {
      setIndustryPick(CUSTOM);
      setIndustryCustom(autoIndustry ?? '');
    }
  };
  const queryOptions = brandId !== '' ? (source.data?.queries ?? []) : [];

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const r = await generate.mutateAsync({
        body: {
          brandId: brandId === '' ? null : brandId,
          companyName,
          representativeName,
          query,
          industry,
        },
      });
      void navigate({ to: '/admin/articles/$id', params: { id: r.article.id } });
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <Card>
      <CardHeader
        title="한 편 쓰기"
        description="고객을 고르면 업체명·대표자명·목표 질문이 채워집니다. 등록 안 된 업체는 직접 입력합니다"
        actions={
          onClose ? (
            <Button size="sm" variant="ghost" onClick={onClose}>
              닫기
            </Button>
          ) : undefined
        }
      />
      <CardBody>
        <form className="grid gap-3 md:grid-cols-2" onSubmit={submit}>
          <Label className="md:col-span-2">
            고객
            <Select
              value={brandId}
              onChange={(e) => {
                setBrandId(e.target.value);
                if (e.target.value === '') {
                  setCompanyName('');
                  setRepresentativeName('');
                  setQuery('');
                }
              }}
            >
              <option value="">직접 입력 (등록 안 된 업체)</option>
              <BrandOptions real={brands.real} test={brands.test} />
            </Select>
          </Label>
          <Label>
            업체명
            <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} required maxLength={120} />
          </Label>
          <Label>
            대표자명
            <Input
              value={representativeName}
              onChange={(e) => setRepresentativeName(e.target.value)}
              required
              maxLength={60}
              placeholder={
                brandId !== '' && source.data && !source.data.representativeName ? '등록된 대표자명이 없습니다' : ''
              }
            />
          </Label>
          <div className="grid gap-1 font-medium text-slate-700 text-sm">
            업종
            {useAuto ? (
              <div className="flex h-9 items-center gap-2">
                <Badge tone="blue">{autoIndustry}</Badge>
                <span className="font-normal text-slate-500 text-xs">업체명에서 자동으로 정했습니다</span>
                <button
                  type="button"
                  className="font-normal text-slate-500 text-xs underline hover:text-slate-900"
                  onClick={overrideIndustry}
                >
                  바꾸기
                </button>
              </div>
            ) : !industryManual && companyName.trim() === '' ? (
              <p className="flex h-9 items-center font-normal text-slate-500 text-xs">
                업체명을 넣으면 업종을 자동으로 정합니다
              </p>
            ) : (
              <>
                <div className="flex gap-2">
                  <Select className="w-40" value={industryPick} onChange={(e) => setIndustryPick(e.target.value)}>
                    <option value="">업종 선택</option>
                    {ARTICLE_INDUSTRY_PRESETS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                    <option value={CUSTOM}>직접 입력</option>
                  </Select>
                  {industryPick === CUSTOM ? (
                    <Input
                      value={industryCustom}
                      onChange={(e) => setIndustryCustom(e.target.value)}
                      required
                      maxLength={40}
                      placeholder="예: 안과"
                    />
                  ) : null}
                </div>
                {industryManual ? (
                  autoIndustry ? (
                    <button
                      type="button"
                      className="w-fit font-normal text-slate-500 text-xs underline hover:text-slate-900"
                      onClick={() => setIndustryManual(false)}
                    >
                      자동({autoIndustry})으로 되돌리기
                    </button>
                  ) : null
                ) : (
                  <span className="font-normal text-slate-500 text-xs">
                    업체명으로 업종을 알 수 없어 직접 골라 주세요
                  </span>
                )}
              </>
            )}
          </div>
          <Label>
            질문{' '}
            {queryOptions.length > 0 ? (
              <span className="font-normal text-slate-500">
                (목표 질문 {queryOptions.length}개 중 고르거나 직접 입력)
              </span>
            ) : null}
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              required
              minLength={2}
              maxLength={300}
              list="article-queries"
              placeholder="예: 분당 임플란트 잘하는 치과 추천해줘"
            />
            <datalist id="article-queries">
              {queryOptions.map((q) => (
                <option key={q} value={q} />
              ))}
            </datalist>
          </Label>
          <div className="flex items-center gap-3 md:col-span-2">
            <Button type="submit" disabled={generate.isPending || industry === ''}>
              {generate.isPending ? '기사 쓰는 중… (1~2분 걸립니다)' : '기사 생성'}
            </Button>
            <FieldError>{error}</FieldError>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

export function ArticleDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const got = useApi(article.getArticle, { params: { id } });
  const save = useApiMutation(article.updateArticle);
  const send = useApiMutation(article.sendArticle);
  const recall = useApiMutation(article.recallArticle);
  const renotify = useApiMutation(article.notifyArticle);
  const del = useApiMutation(article.deleteArticle);
  const removePublication = useApiMutation(article.deleteArticlePublication);

  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [body, setBody] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!got.data) return;
    setTitle(got.data.article.title);
    setSubtitle(got.data.article.subtitle);
    setBody(got.data.article.body);
  }, [got.data]);

  if (got.error) return <ErrorBox error={got.error} />;
  if (!got.data) return <Loading />;
  const a = got.data.article;
  const editable = a.status === 'draft';
  const dirty = title !== a.title || subtitle !== a.subtitle || body !== a.body;
  const pending = (body.match(/\[확인 필요[^\]]*\]/g) ?? []).length;

  const run = async (job: () => Promise<unknown>, done: string) => {
    setError(null);
    setNotice(null);
    try {
      await job();
      setNotice(done);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const onRemovePublication = async (p: ArticlePublication) => {
    if (!confirm(`직접 입력한 게재를 지웁니다.\n${p.outlet} · ${p.url}`)) return;
    await run(
      () => removePublication.mutateAsync({ params: { id }, query: { publicationId: p.id } }),
      '게재를 지웠습니다',
    );
  };

  const onSave = () => run(() => save.mutateAsync({ params: { id }, body: { title, subtitle, body } }), '저장했습니다');
  const onSend = async () => {
    if (dirty) return setError('고친 내용을 먼저 저장해 주세요');
    if (
      !confirm(
        `${a.brandName ?? a.companyName} 광고주에게 검토를 요청합니다. 보낸 뒤에는 회수하기 전까지 고칠 수 없습니다.`,
      )
    )
      return;
    await run(() => send.mutateAsync({ params: { id } }), '광고주에게 검토를 요청했습니다');
  };
  const onRecall = async () => {
    if (!confirm('검토 요청을 회수합니다. 광고주 화면에서 이 기사가 사라지고, 광고주가 고친 내용은 원고에 남습니다.'))
      return;
    await run(() => recall.mutateAsync({ params: { id } }), '회수했습니다 — 다시 고칠 수 있습니다');
  };
  const onCopy = async () => {
    await navigator.clipboard.writeText([title, subtitle, body].filter((s) => s.trim() !== '').join('\n\n'));
    setNotice('제목·부제·본문을 복사했습니다');
  };
  const onDelete = async () => {
    if (!confirm('이 기사를 삭제합니다.')) return;
    await del.mutateAsync({ params: { id } });
    void navigate({ to: '/admin/articles' });
  };

  const edited = a.reviewerEditedAt !== null && a.sentBody !== null;

  const published = a.status === 'published';
  const actions = (
    <div className="flex flex-wrap gap-2">
      {editable ? (
        <>
          <Button size="sm" onClick={() => void onSave()} disabled={!dirty || save.isPending}>
            {save.isPending ? '저장 중…' : '저장'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => void onSend()} disabled={!a.brandId || send.isPending}>
            광고주에게 보내기
          </Button>
        </>
      ) : null}
      {a.status === 'sent' ? (
        <Button size="sm" variant="outline" onClick={() => void onRecall()} disabled={recall.isPending}>
          회수
        </Button>
      ) : null}
      {a.status === 'approved' || a.status === 'published' ? (
        <Button size="sm" onClick={() => window.open(DEPLOY_ORDER_URL, '_blank', 'noopener,noreferrer')}>
          배포신청
        </Button>
      ) : null}
      <Button size="sm" variant="outline" onClick={() => void onCopy()}>
        복사
      </Button>
      <Button size="sm" variant="ghost" onClick={() => void onDelete()}>
        삭제
      </Button>
    </div>
  );

  return (
    <>
      <PageTitle
        title="기사"
        description={`${a.companyName} · ${a.industry} · ${dateTime(a.createdAt)} 생성`}
        actions={
          <Link to="/admin/articles" className="text-sm text-slate-500 hover:text-slate-900">
            ← 기사 목록
          </Link>
        }
      />
      <Card>
        <CardBody className="grid gap-1 text-sm text-slate-600">
          <p className="flex flex-wrap items-center gap-2">
            <ArticleStatusBadge status={a.status} />
            {a.sentAt ? <span>보낸 날 {dateTime(a.sentAt)}</span> : null}
            {a.reviewerEditedAt ? <Badge tone="blue">광고주 수정 {dateTime(a.reviewerEditedAt)}</Badge> : null}
            {a.approvedAt ? <span>· 승인 {dateTime(a.approvedAt)}</span> : null}
            {a.publishedAt ? <span>· 게재 확인 {dateTime(a.publishedAt)}</span> : null}
          </p>
          {a.pressOrders.length > 0 ? (
            <p className="flex flex-wrap items-center gap-2">
              <span>배포 신청(mediap):</span>
              {a.pressOrders.map((o) => (
                <Badge key={`${o.outlet}-${o.status}-${o.scheduledOn}`} className="bg-violet-100 text-violet-800">
                  {o.outlet} · {o.status}
                  {o.scheduledOn ? ` · ${o.scheduledOn.slice(5).replace('-', '.')} 예약` : ''}
                </Badge>
              ))}
            </p>
          ) : null}
          {a.status !== 'draft' ? (
            <NoticeLine
              notices={a.notices}
              canResend={a.status === 'sent'}
              busy={renotify.isPending}
              onResend={() =>
                void run(
                  () => renotify.mutateAsync({ params: { id } }),
                  '알림톡을 다시 보냈습니다 — 결과는 위 줄에 보입니다',
                )
              }
            />
          ) : null}
          <p>
            질문: <span className="font-medium text-slate-900">{a.query}</span>
          </p>
          <p>
            대표자: {a.representativeName}
            {a.brandName ? ` · 고객: ${a.brandName}` : ' · 등록 안 된 업체 (광고주에게 보낼 수 없습니다)'} · 모델:{' '}
            {a.model}
          </p>
          {pending > 0 ? (
            <p>
              <Badge tone="amber">확인 필요 {pending}곳</Badge>{' '}
              <span className="text-slate-500">
                본문의 [확인 필요: …] 자리를 사실로 채우거나 문장을 지운 뒤 보내 주세요
              </span>
            </p>
          ) : null}
        </CardBody>
      </Card>

      {edited ? (
        <Card>
          <CardHeader
            title="광고주 수정 내역"
            description="보낸 원고와 광고주가 고친 원고를 대조합니다 — 빨간 취소선은 지운 말, 초록 밑줄은 넣은 말입니다"
          />
          <CardBody className="grid gap-4">
            <div>
              <p className="mb-1 font-medium text-slate-500 text-xs">제목</p>
              <ArticleDiff before={a.sentTitle ?? ''} after={a.title} />
            </div>
            <div>
              <p className="mb-1 font-medium text-slate-500 text-xs">본문</p>
              <ArticleDiff before={a.sentBody ?? ''} after={a.body} />
            </div>
          </CardBody>
        </Card>
      ) : null}

      {/* 광고주 승인 전에 배포가 나가기도 한다 — 보낸 기사(검토 중)부터 게재를 보이고 넣는다 */}
      {a.status !== 'draft' ? (
        <Card>
          <CardHeader
            title={a.publications.length > 0 ? `언론사 게재 ${a.publications.length}곳` : '언론사 게재'}
            description={
              a.publications.length > 0
                ? '배포 대행사에서 노출완료로 확인됐거나 직접 입력한 게재입니다'
                : '배포신청 뒤 대행사에서 노출완료가 되면 매일 11시(또는 기사 목록의 "게재 확인")에 여기 붙습니다'
            }
          />
          <CardBody className="grid gap-4">
            {a.publications.length > 0 ? (
              <PublicationList publications={a.publications} onRemove={(p) => void onRemovePublication(p)} />
            ) : null}
            <ManualPublicationForm articleId={a.id} />
          </CardBody>
        </Card>
      ) : null}

      {a.status !== 'draft' && !published ? (
        <Card>
          <CardHeader
            title={`사진 ${a.photos.length}장`}
            description={
              a.photos.length > 0
                ? '눌러서 원본을 받습니다'
                : '광고주가 올린 사진이 없습니다 — 기사는 사진이 필수라 광고주가 사진을 올려야 승인할 수 있습니다'
            }
          />
          {a.photos.length > 0 ? (
            <CardBody>
              <PhotoGrid articleId={a.id} photos={a.photos} realm="admin" />
            </CardBody>
          ) : null}
        </Card>
      ) : null}

      {/* 게재 완료면 사진·원고·미리보기를 접는다 — 실린 기사는 위 언론사 링크로 본다 */}
      {published ? null : (
        <div className="grid gap-4 xl:grid-cols-2">
          <Card>
            <CardHeader
              title={editable ? '편집' : '원고 (광고주 검토 중·승인 뒤에는 고칠 수 없습니다)'}
              actions={actions}
            />
            <CardBody className="grid gap-3">
              <Label>
                제목
                <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} disabled={!editable} />
              </Label>
              <Label>
                부제
                <Input
                  value={subtitle}
                  onChange={(e) => setSubtitle(e.target.value)}
                  maxLength={300}
                  disabled={!editable}
                />
              </Label>
              <Label>
                본문 (문단은 빈 줄로)
                <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={24} disabled={!editable} />
              </Label>
              <p className="text-xs text-slate-500">
                본문 {body.replace(/\s/g, '').length.toLocaleString()}자 (공백 제외)
              </p>
              {notice ? <p className="text-sm text-emerald-700">{notice}</p> : null}
              <FieldError>{error}</FieldError>
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="미리보기" />
            <CardBody>
              <ArticlePreview title={title} subtitle={subtitle} body={body} />
            </CardBody>
          </Card>
        </div>
      )}
    </>
  );
}

/**
 * 게재 직접 입력 — mediap 에서 못 가져온 게재(대행사 밖에서 실린 기사·목록에서 빠진 신청)를 손으로 넣는다.
 * 넣으면 기사가 게재 완료가 되고 광고주 화면에도 보인다. 같은 주소는 두 번 넣을 수 없다.
 */
function ManualPublicationForm({ articleId }: { articleId: string }) {
  const add = useApiMutation(article.addArticlePublication);
  const [outlet, setOutlet] = useState('');
  const [url, setUrl] = useState('');
  const [scheduledOn, setScheduledOn] = useState('');
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await add.mutateAsync({
        params: { id: articleId },
        body: { outlet, url, title, scheduledOn: scheduledOn === '' ? null : scheduledOn },
      });
      setOutlet('');
      setUrl('');
      setScheduledOn('');
      setTitle('');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <form className="grid gap-3 rounded-lg border border-slate-200 border-dashed p-3 md:grid-cols-2" onSubmit={submit}>
      <p className="font-medium text-slate-900 text-sm md:col-span-2">
        게재 직접 입력 <span className="font-normal text-slate-500">— mediap 에서 못 가져온 게재를 넣습니다</span>
      </p>
      <Label>
        언론사
        <Input
          value={outlet}
          onChange={(e) => setOutlet(e.target.value)}
          required
          maxLength={60}
          placeholder="예: 매일안전신문"
        />
      </Label>
      <Label>
        게재일 <span className="font-normal text-slate-500">(모르면 비워 둡니다)</span>
        <Input type="date" value={scheduledOn} onChange={(e) => setScheduledOn(e.target.value)} />
      </Label>
      <Label className="md:col-span-2">
        기사 주소
        <Input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          required
          maxLength={2000}
          placeholder="https://"
        />
      </Label>
      <Label className="md:col-span-2">
        실린 제목 <span className="font-normal text-slate-500">(비우면 이 기사 제목을 씁니다)</span>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} />
      </Label>
      <div className="flex items-center gap-3 md:col-span-2">
        <Button type="submit" size="sm" disabled={add.isPending}>
          {add.isPending ? '넣는 중…' : '게재 추가'}
        </Button>
        <FieldError>{error}</FieldError>
      </div>
    </form>
  );
}

/**
 * 광고주 알림(알림톡) — 기사를 보낼 때 원장님께 갔는지. 가장 최근 결과 하나를 보이고, 나머지는 마우스를 올리면.
 * 검토 중이면 다시 보낼 수 있다(번호를 고쳤거나 템플릿 승인 뒤).
 */
function NoticeLine({
  notices,
  canResend,
  busy,
  onResend,
}: {
  notices: ArticleNotice[];
  canResend: boolean;
  busy: boolean;
  onResend: () => void;
}) {
  const last = notices[0];
  const label = !last
    ? '보낸 기록 없음'
    : last.status === 'sent'
      ? `${last.recipient ?? ''} · 보냄 ${dateTime(last.at)}`
      : last.status === 'failed'
        ? `${last.recipient ?? ''} · 실패 — ${last.detail ?? ''}`
        : `보내지 않음 — ${last.detail ?? ''}`;
  const tone = !last ? 'slate' : last.status === 'sent' ? 'green' : last.status === 'failed' ? 'red' : 'amber';
  const history = notices
    .map((n) => `${dateTime(n.at)} ${n.recipient ?? ''} ${n.status}${n.detail ? ` (${n.detail})` : ''}`)
    .join('\n');
  return (
    <p className="flex flex-wrap items-center gap-2" title={history || undefined}>
      <span>원장님 알림(알림톡):</span>
      <Badge tone={tone}>{label}</Badge>
      {canResend ? (
        <button
          type="button"
          className="text-slate-500 text-xs underline hover:text-slate-900"
          onClick={onResend}
          disabled={busy}
        >
          {busy ? '보내는 중…' : '다시 보내기'}
        </button>
      ) : null}
    </p>
  );
}
